
import React, { createContext, useState, useEffect, useCallback, useRef, useContext } from "react";
import { listPatients, createPatient, updatePatient, deletePatient, bulkImport } from "../services/api";
import { useAuth, can } from "./useAuth";

// ── Global patient data context ──────────────────────────────────────────
// Single fetch shared across ALL pages. Eliminates the "Loading..." screen
// on every tab switch by caching data in React context.
const PatientsCtx = createContext(null);

export function PatientsProvider({ children }) {
  const { role, email } = useAuth();
  const [patients, setPatients]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [saving, setSaving]       = useState(false);
  const initialLoaded = useRef(false);

  const load = useCallback(async (retryCount = 0) => {
    // After the first successful load, do NOT show the blocking loading state.
    // Instead, fetch silently in the background so the cached UI stays visible.
    if (initialLoaded.current) {
      // silent background refresh — no loading spinner
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const filters = can(role, "viewAllPatients") && !can(role, "viewOnlyOwnAE")
        ? {}
        : { accountExec: email };  // AEs only see their own
      const data = await listPatients(filters);
      const raw = Array.isArray(data) ? data
        : Array.isArray(data?.items) ? data.items
        : Array.isArray(data?.patients) ? data.patients
        : [];
      // Strip legacy 2025 test/demo data — only show 2026+ referrals
      const items = raw.filter(p => {
        const d = p.date || p.refDate || p.createdAt || "";
        if (!d) return true; // keep records with no date (will be triaged manually)
        const yr = new Date(d).getFullYear();
        return yr >= 2026 || isNaN(yr);
      });
      setPatients(items);
      setError(null);
      initialLoaded.current = true;
      setLoading(false);
    } catch (e) {
      console.error("[PatientsContext] load failed:", e.message, "retry:", retryCount);
      // Auto-retry up to 3 times with backoff — handles the auth-token race on a
      // fresh page load (token not warm yet). Keep the loading state up while a
      // retry is pending; only surface the error once all attempts are exhausted.
      if (retryCount < 3) {
        const delay = 800 * Math.pow(2, retryCount); // 0.8s, 1.6s, 3.2s
        setTimeout(() => load(retryCount + 1), delay);
        return; // retry pending — don't clear loading, don't set error yet
      }
      setError(e.message || "Failed to load patients");
      setLoading(false);
    }
  }, [role, email]);

  useEffect(() => {
    load();

    // Poll every 60s — but ONLY when:
    // 1. Tab is visible (user is looking at the page)
    // 2. No form is open (window._vitalcarePollPaused)
    const interval = setInterval(() => {
      if (!window._vitalcarePollPaused && document.visibilityState === "visible") {
        load();
      }
    }, 60000);

    // When user returns to tab after being away — reload immediately
    // but only if away for more than 2 minutes
    let hiddenAt = null;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (document.visibilityState === "visible" && hiddenAt) {
        const awayMs = Date.now() - hiddenAt;
        hiddenAt = null;
        // Only reload if away >2 min AND no form open
        if (awayMs > 120000 && !window._vitalcarePollPaused) {
          load();
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [load]);

  const add = useCallback(async (data) => {
    setSaving(true);
    try {
      const result = await createPatient(data);
      setPatients(p => [result, ...p]);
      return result;
    } finally { setSaving(false); }
  }, []);

  const update = useCallback(async (id, data) => {
    setSaving(true);
    try {
      const result = await updatePatient(id, data);
      setPatients(p => p.map(x => x.patientId === id ? result : x));
      return result;
    } finally { setSaving(false); }
  }, []);

  const remove = useCallback(async (id) => {
    setSaving(true);
    try {
      await deletePatient(id);
      setPatients(p => p.filter(x => x.patientId !== id));
    } finally { setSaving(false); }
  }, []);

  const importBulk = useCallback(async (records) => {
    setSaving(true);
    try {
      const result = await bulkImport(records);
      await load();
      return result;
    } finally { setSaving(false); }
  }, [load]);

  const value = { patients, loading, error, saving, reload: load, add, update, remove, importBulk };

  return <PatientsCtx.Provider value={value}>{children}</PatientsCtx.Provider>;
}

export function usePatientsContext() {
  const ctx = useContext(PatientsCtx);
  if (!ctx) throw new Error("usePatientsContext must be inside <PatientsProvider>");
  return ctx;
}
